import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings as SettingsIcon, Save, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({});
  const [checklist, setChecklist] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [waitTimes, setWaitTimes] = useState([]);
  const [newWaitTime, setNewWaitTime] = useState('');
  const [editingWaitTime, setEditingWaitTime] = useState(null);
  const [editWaitTimeValue, setEditWaitTimeValue] = useState('');
  const [overdueDays, setOverdueDays] = useState(30);
  const [shopInfo, setShopInfo] = useState({ name: '', address: '', phone: '', rfc: '', website: '', footer: '' });
  const [cashSettings, setCashSettings] = useState({ usd_mxn_rate: '17.50', cash_register_threshold: '20', thermal_paper_width: '80' });
  const [saved, setSaved] = useState('');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings`, { headers: getAuthHeaders() });
      setSettings(res.data);
      setChecklist(res.data.repair_checklist || []);
      setExpenseCategories(res.data.expense_categories || []);
      setWaitTimes(res.data.wait_times || []);
      setOverdueDays(res.data.credit_overdue_days || 30);
      setShopInfo({
        name: res.data.shop_info?.name || '',
        address: res.data.shop_info?.address || '',
        phone: res.data.shop_info?.phone || '',
        rfc: res.data.shop_info?.rfc || '',
        website: res.data.shop_info?.website || '',
        footer: res.data.shop_info?.footer || ''
      });
      setCashSettings({
        usd_mxn_rate: String(res.data.usd_mxn_rate ?? '17.50'),
        cash_register_threshold: String(res.data.cash_register_threshold ?? '20'),
        thermal_paper_width: String(res.data.thermal_paper_width ?? '80'),
      });
    } catch (err) { console.error(err); }
  };

  const saveSetting = async (key, value) => {
    try {
      await axios.put(`${API}/api/settings/${key}`, { value }, { headers: getAuthHeaders() });
      setSaved(key);
      setTimeout(() => setSaved(''), 2000);
    } catch (err) { console.error(err); }
  };

  // Checklist
  const addChecklistItem = () => {
    if (!newItem.trim()) return;
    const updated = [...checklist, newItem.trim()];
    setChecklist(updated);
    setNewItem('');
    saveSetting('repair_checklist', updated);
  };
  const removeChecklistItem = (idx) => {
    const updated = checklist.filter((_, i) => i !== idx);
    setChecklist(updated);
    saveSetting('repair_checklist', updated);
  };

  // Expense categories
  const addCategory = () => {
    if (!newCategory.trim()) return;
    const updated = [...expenseCategories, newCategory.trim()];
    setExpenseCategories(updated);
    setNewCategory('');
    saveSetting('expense_categories', updated);
  };
  const removeCategory = (idx) => {
    const updated = expenseCategories.filter((_, i) => i !== idx);
    setExpenseCategories(updated);
    saveSetting('expense_categories', updated);
  };

  // Wait times
  const addWaitTime = () => {
    if (!newWaitTime.trim()) return;
    const updated = [...waitTimes, newWaitTime.trim()];
    setWaitTimes(updated);
    setNewWaitTime('');
    saveSetting('wait_times', updated);
  };
  const removeWaitTime = (idx) => {
    const updated = waitTimes.filter((_, i) => i !== idx);
    setWaitTimes(updated);
    saveSetting('wait_times', updated);
  };
  const startEditWaitTime = (idx) => {
    setEditingWaitTime(idx);
    setEditWaitTimeValue(waitTimes[idx]);
  };
  const saveEditWaitTime = () => {
    if (!editWaitTimeValue.trim()) return;
    const updated = waitTimes.map((w, i) => i === editingWaitTime ? editWaitTimeValue.trim() : w);
    setWaitTimes(updated);
    setEditingWaitTime(null);
    setEditWaitTimeValue('');
    saveSetting('wait_times', updated);
  };

  return (
    <div data-testid="settings-page">
      <div className="flex items-center gap-3 mb-6">
        <SettingsIcon className="w-6 h-6 text-[#1D3C6E]" />
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Configuracion</h1>
      </div>

      <Tabs defaultValue="checklist" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="checklist">Checklist Reparaciones</TabsTrigger>
          <TabsTrigger value="wait_times">Tiempos de Espera</TabsTrigger>
          <TabsTrigger value="expenses">Categorias de Gastos</TabsTrigger>
          <TabsTrigger value="credits">Creditos</TabsTrigger>
          <TabsTrigger value="shop">Empresa / Ticket</TabsTrigger>
          <TabsTrigger value="cash" data-testid="settings-tab-cash">Caja</TabsTrigger>
          <TabsTrigger value="devices" data-testid="settings-tab-devices">Dispositivos Checador</TabsTrigger>
        </TabsList>

        <TabsContent value="checklist">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Items del Checklist de Inspeccion</h3>
            <div className="space-y-2 mb-4">
              {checklist.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  <span className="text-sm">{item}</span>
                  <button onClick={() => removeChecklistItem(idx)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Nuevo item..." value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addChecklistItem()} />
              <Button onClick={addChecklistItem} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="add-checklist-item">Agregar</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="wait_times">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Opciones de Tiempo de Espera</h3>
            <div className="space-y-2 mb-4">
              {waitTimes.map((wt, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  {editingWaitTime === idx ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input className="h-7 text-sm" value={editWaitTimeValue} onChange={(e) => setEditWaitTimeValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEditWaitTime()} />
                      <Button size="sm" onClick={saveEditWaitTime} className="h-7 text-xs bg-[#1D3C6E]">OK</Button>
                      <button onClick={() => setEditingWaitTime(null)} className="text-xs text-slate-400">Cancelar</button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm">{wt}</span>
                      <div className="flex gap-2">
                        <button onClick={() => startEditWaitTime(idx)} className="text-xs text-[#1D3C6E] hover:underline">Editar</button>
                        <button onClick={() => removeWaitTime(idx)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {waitTimes.length === 0 && <p className="text-xs text-slate-400">No hay tiempos configurados</p>}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Ej: 1 hora, 3 dias, 1 semana..." value={newWaitTime} onChange={(e) => setNewWaitTime(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addWaitTime()} />
              <Button onClick={addWaitTime} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="add-wait-time">Agregar</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="expenses">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Categorias de Gastos</h3>
            <div className="space-y-2 mb-4">
              {expenseCategories.map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  <span className="text-sm">{cat}</span>
                  <button onClick={() => removeCategory(idx)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Nueva categoria..." value={newCategory} onChange={(e) => setNewCategory(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCategory()} />
              <Button onClick={addCategory} className="bg-[#1D3C6E] hover:bg-[#152B50]">Agregar</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="credits">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Configuracion de Creditos</h3>
            <div className="max-w-sm">
              <label className="text-sm font-medium text-slate-600 mb-2 block">Dias para marcar como vencido</label>
              <div className="flex gap-2">
                <Input type="number" value={overdueDays} onChange={(e) => setOverdueDays(parseInt(e.target.value) || 30)} />
                <Button onClick={() => saveSetting('credit_overdue_days', overdueDays)} className="bg-[#1D3C6E] hover:bg-[#152B50]">
                  <Save className="w-4 h-4" />
                </Button>
              </div>
              {saved === 'credit_overdue_days' && <p className="text-xs text-green-600 mt-1">Guardado</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="shop">
          <div className="bg-white rounded-lg border border-slate-200 p-5 max-w-xl">
            <h3 className="font-semibold text-slate-900 mb-1">Datos de la Empresa</h3>
            <p className="text-xs text-slate-500 mb-4">Esta informacion se imprimira en los tickets termicos de POS y de reparaciones.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre comercial</label>
                <Input data-testid="shop-name-input" value={shopInfo.name} onChange={(e) => setShopInfo({ ...shopInfo, name: e.target.value })} placeholder="Fixif - Servicio Tecnico" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Direccion</label>
                <Input data-testid="shop-address-input" value={shopInfo.address} onChange={(e) => setShopInfo({ ...shopInfo, address: e.target.value })} placeholder="Calle Falsa 123, Col. Centro" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Telefono</label>
                  <Input data-testid="shop-phone-input" value={shopInfo.phone} onChange={(e) => setShopInfo({ ...shopInfo, phone: e.target.value })} placeholder="55 1234 5678" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">RFC (opcional)</label>
                  <Input data-testid="shop-rfc-input" value={shopInfo.rfc} onChange={(e) => setShopInfo({ ...shopInfo, rfc: e.target.value })} placeholder="XAXX010101000" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Sitio web / Redes (opcional)</label>
                <Input data-testid="shop-website-input" value={shopInfo.website} onChange={(e) => setShopInfo({ ...shopInfo, website: e.target.value })} placeholder="www.fixif.mx" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Mensaje de pie de ticket</label>
                <Input data-testid="shop-footer-input" value={shopInfo.footer} onChange={(e) => setShopInfo({ ...shopInfo, footer: e.target.value })} placeholder="Gracias por su preferencia." />
              </div>
              <Button onClick={() => saveSetting('shop_info', shopInfo)} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="save-shop-info">
                <Save className="w-4 h-4 mr-1" /> Guardar
              </Button>
              {saved === 'shop_info' && <p className="text-xs text-green-600">Guardado</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cash">
          <div className="bg-white rounded-lg border border-slate-200 p-5 max-w-xl space-y-4">
            <h3 className="font-semibold text-slate-900">Configuracion de Caja</h3>
            <p className="text-xs text-slate-500">Parametros usados en la apertura, arqueo y cierre de caja.</p>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Tipo de cambio USD → MXN</label>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={cashSettings.usd_mxn_rate}
                  onChange={(e) => setCashSettings({ ...cashSettings, usd_mxn_rate: e.target.value })}
                  data-testid="cash-set-usd-rate" />
                <Button onClick={() => saveSetting('usd_mxn_rate', parseFloat(cashSettings.usd_mxn_rate) || 0)} className="bg-[#1D3C6E]"><Save className="w-4 h-4" /></Button>
              </div>
              {saved === 'usd_mxn_rate' && <p className="text-xs text-green-600 mt-1">Guardado</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Umbral de tolerancia (semaforo amarillo, MXN)</label>
              <p className="text-[11px] text-slate-400 mb-1">Diferencias dentro de este monto se marcan como "diferencia menor".</p>
              <div className="flex gap-2">
                <Input type="number" step="0.01" value={cashSettings.cash_register_threshold}
                  onChange={(e) => setCashSettings({ ...cashSettings, cash_register_threshold: e.target.value })}
                  data-testid="cash-set-threshold" />
                <Button onClick={() => saveSetting('cash_register_threshold', parseFloat(cashSettings.cash_register_threshold) || 0)} className="bg-[#1D3C6E]"><Save className="w-4 h-4" /></Button>
              </div>
              {saved === 'cash_register_threshold' && <p className="text-xs text-green-600 mt-1">Guardado</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Ancho de impresora termica</label>
              <div className="flex gap-2">
                <select className="flex-1 h-9 px-2 border border-slate-200 rounded-md text-sm" value={cashSettings.thermal_paper_width}
                  onChange={(e) => setCashSettings({ ...cashSettings, thermal_paper_width: e.target.value })} data-testid="cash-set-width">
                  <option value="58">58 mm</option>
                  <option value="80">80 mm</option>
                </select>
                <Button onClick={() => saveSetting('thermal_paper_width', parseInt(cashSettings.thermal_paper_width) || 80)} className="bg-[#1D3C6E]"><Save className="w-4 h-4" /></Button>
              </div>
              {saved === 'thermal_paper_width' && <p className="text-xs text-green-600 mt-1">Guardado</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="devices">
          <ClockDevices />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClockDevices() {
  const { getAuthHeaders } = useAuth();
  const [devices, setDevices] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentToken, setCurrentToken] = useState(localStorage.getItem('fixif_clock_token') || '');
  const [adminPin, setAdminPin] = useState('');
  const [adminPinSaved, setAdminPinSaved] = useState(false);

  const load = async () => {
    try {
      const [devRes, pinRes] = await Promise.all([
        axios.get(`${API}/api/timeclock/devices`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/settings/clock_admin_pin`, { headers: getAuthHeaders() }).catch(() => ({ data: { value: '' } })),
      ]);
      setDevices(devRes.data);
      setAdminPin(String(pinRes.data?.value || ''));
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveAdminPin = async () => {
    setError('');
    if (adminPin && (adminPin.length !== 6 || !/^\d{6}$/.test(adminPin))) {
      setError('El PIN debe ser exactamente 6 digitos');
      return;
    }
    try {
      await axios.put(`${API}/api/settings/clock_admin_pin`, { value: adminPin }, { headers: getAuthHeaders() });
      setAdminPinSaved(true);
      setTimeout(() => setAdminPinSaved(false), 2500);
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const registerThis = async () => {
    if (!name.trim()) { setError('Nombre requerido'); return; }
    setError(''); setSuccess(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/timeclock/devices`, { name: name.trim() }, { headers: getAuthHeaders() });
      localStorage.setItem('fixif_clock_token', r.data.token);
      setCurrentToken(r.data.token);
      setSuccess(`Dispositivo "${r.data.name}" registrado y vinculado a este navegador.`);
      setName('');
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  const registerOther = async () => {
    if (!name.trim()) { setError('Nombre requerido'); return; }
    setError(''); setSuccess(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/timeclock/devices`, { name: name.trim() }, { headers: getAuthHeaders() });
      // Prompt admin to copy token (one-time view)
      window.prompt(`Token del dispositivo "${r.data.name}". Copia este valor y pegalo en el otro navegador (en localStorage como "fixif_clock_token"):`, r.data.token);
      setSuccess(`Dispositivo "${r.data.name}" creado. Recuerda guardar el token en el dispositivo destino.`);
      setName('');
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  const toggleActive = async (dev) => {
    try {
      await axios.put(`${API}/api/timeclock/devices/${dev.id}`, { active: !dev.active }, { headers: getAuthHeaders() });
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const remove = async (dev) => {
    if (!window.confirm(`¿Eliminar dispositivo "${dev.name}"? El navegador correspondiente perdera el acceso al checador.`)) return;
    try {
      await axios.delete(`${API}/api/timeclock/devices/${dev.id}`, { headers: getAuthHeaders() });
      if (currentToken === dev.token) {
        localStorage.removeItem('fixif_clock_token');
        setCurrentToken('');
      }
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const unlinkThisBrowser = () => {
    if (!window.confirm('¿Desvincular este navegador? Tendras que volver a registrar este dispositivo para acceder al checador.')) return;
    localStorage.removeItem('fixif_clock_token');
    setCurrentToken('');
  };

  const isLinked = !!currentToken;
  const linkedDevice = devices.find(d => d.token === currentToken);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 max-w-3xl space-y-5" data-testid="settings-devices-section">
      <div>
        <h3 className="font-semibold text-slate-900">Dispositivos autorizados para el Checador</h3>
        <p className="text-xs text-slate-500 mt-1">
          Solo los dispositivos en esta lista pueden acceder a la pantalla <code className="text-[11px] bg-slate-100 px-1 rounded">/clock</code>. Si la lista esta vacia, cualquier dispositivo puede acceder hasta que registres el primero.
        </p>
      </div>

      {/* This browser status */}
      <div className={`rounded-lg p-4 border ${isLinked ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: isLinked ? '#047857' : '#92400e' }}>
          Estado de este navegador
        </p>
        {isLinked ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-emerald-900">
              Vinculado como <strong>{linkedDevice?.name || 'desconocido'}</strong>
              {linkedDevice && !linkedDevice.active && <span className="ml-2 text-rose-600">(desactivado)</span>}
            </p>
            <Button size="sm" variant="outline" onClick={unlinkThisBrowser} data-testid="unlink-this-browser">Desvincular</Button>
          </div>
        ) : (
          <p className="text-sm text-amber-900">
            Este navegador <strong>no esta vinculado</strong> a ningun dispositivo. Registra uno para usar el checador desde aqui.
          </p>
        )}
      </div>

      {/* Admin PIN for kiosk-side registration */}
      <div className="border border-slate-200 rounded-lg p-4 space-y-2">
        <h4 className="text-sm font-semibold text-slate-700">PIN de administrador</h4>
        <p className="text-xs text-slate-500">
          Este PIN de 6 digitos permite registrar nuevos dispositivos directamente desde la pantalla <code className="text-[11px] bg-slate-100 px-1 rounded">/clock</code> sin tener que iniciar sesion. Dejalo vacio para deshabilitar el registro por PIN.
        </p>
        <div className="flex gap-2 max-w-xs">
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={adminPin}
            onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="text-center font-mono tracking-[0.4em] text-lg"
            data-testid="admin-pin-input"
          />
          <Button onClick={saveAdminPin} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="save-admin-pin">
            <Save className="w-4 h-4" />
          </Button>
        </div>
        {adminPinSaved && <p className="text-xs text-emerald-600">PIN guardado</p>}
        {!adminPin && <p className="text-[11px] text-amber-700">Sin PIN configurado · registro por PIN deshabilitado.</p>}
      </div>

      {/* Register form */}
      <div className="border border-slate-200 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-slate-700">Registrar un dispositivo</h4>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Tablet Recepcion" data-testid="device-name-input" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={registerThis} disabled={busy} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="register-this-device-btn">
            Registrar este dispositivo
          </Button>
          <Button onClick={registerOther} disabled={busy} variant="outline" data-testid="register-other-device-btn">
            Crear token para otro dispositivo
          </Button>
        </div>
        {success && <p className="text-xs text-emerald-600">{success}</p>}
        {error && <p className="text-xs text-rose-600" data-testid="device-error">{error}</p>}
      </div>

      {/* Devices list */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Nombre</th>
              <th className="text-left px-3 py-2.5">Creado</th>
              <th className="text-left px-3 py-2.5">Ultimo uso</th>
              <th className="text-center px-3 py-2.5">Estatus</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-xs">Sin dispositivos registrados</td></tr>
            ) : devices.map(d => (
              <tr key={d.id} className={`border-t border-slate-100 ${currentToken === d.token ? 'bg-blue-50/50' : ''}`}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-slate-800">{d.name}</div>
                  {currentToken === d.token && <div className="text-[10px] text-blue-600 uppercase tracking-wider">Este navegador</div>}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString('es-MX')} · {d.created_by}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{d.last_seen ? new Date(d.last_seen).toLocaleString('es-MX') : '—'}</td>
                <td className="px-3 py-2.5 text-center">
                  <span className={`status-badge ${d.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {d.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => toggleActive(d)} className="text-xs text-slate-500 hover:underline mr-3" data-testid={`device-toggle-${d.id}`}>
                    {d.active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button onClick={() => remove(d)} className="text-xs text-red-500 hover:text-red-700" data-testid={`device-delete-${d.id}`}>Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
